/**
 * Pure metric formatter — converts raw numeric values into `UnitInfo`:
 * `amount` (display string), `unit` (display label), and `rawAmount`
 * (raw input in the base unit — Mbps for throughput, ms for latency —
 * fed to the smoother for hysteresis).
 *
 * Both auto and lock-mbps branches write the RAW value to `rawAmount`;
 * a previous shape wrote the scaled amount there in one branch, and
 * the smoother + formatter double-laddered into a unit one rung below
 * correct. The contract reads uniformly now: `rawAmount` is what the
 * smoother consumes; `amount` + `unit` are what the DOM renders.
 *
 * Two pure ladders + one rung-picker + two typed wrappers + one
 * dispatcher. Variant coupling stays at the call-site (the result-bridge
 * and `useMetricFormatter`) — the formatter itself is variant-agnostic
 * and takes a unit-policy enum.
 */

import { stripTrailingZeros } from "@utils/utils.ts";
import { isMetricId, type MetricId } from "@src/types/speedtest.ts";

import type { UnitInfo } from "./types.ts";

// ── Ladders ──────────────────────────────────────────────────────────

/** A magnitude rung in a decade ladder. `divisor` is the multiplicative
 *  factor from the base unit (the unit at `divisor === 1`). */
export interface UnitRung {
    unit: string;
    divisor: number;
}

/** Throughput ladder. Mbps is the engine's wire unit (base). Falls
 *  through to Tbps for very fast links. */
export const THROUGHPUT_LADDER = [
    { unit: "kbps", divisor: 1e-3 },
    { unit: "Mbps", divisor: 1 }, // base — engine wire unit
    { unit: "Gbps", divisor: 1e3 },
    { unit: "Tbps", divisor: 1e6 },
] as const satisfies readonly UnitRung[];

/** Latency ladder. ms is the engine's wire unit (base). The µs rung is
 *  defensive against future sub-ms instrumentation; the engine rounds to
 *  integer ms today. */
export const LATENCY_LADDER = [
    { unit: "µs", divisor: 1e-3 },
    { unit: "ms", divisor: 1 }, // base — engine wire unit
    { unit: "s", divisor: 1e3 },
] as const satisfies readonly UnitRung[];

// ── Rung picker ──────────────────────────────────────────────────────

/** Pick the rung whose scaled amount lands in `[1, 1000)`. Falls through
 *  to the largest rung if even that's not enough. Returns the smallest
 *  rung when the value is below the smallest divisor. Zero collapses to
 *  the base rung (the rung with `divisor === 1`). */
export function pickRung<L extends readonly UnitRung[]>(
    amount: number,
    ladder: L,
): L[number] {
    const base = (ladder.find((r) => r.divisor === 1) ?? ladder[0]) as L[number];
    if (!Number.isFinite(amount) || amount === 0) return base;

    const abs = Math.abs(amount);
    for (let i = ladder.length - 1; i >= 0; i--) {
        const rung = ladder[i] as L[number];
        if (abs / rung.divisor >= 1) return rung;
    }
    return ladder[0] as L[number];
}

// ── Policies ─────────────────────────────────────────────────────────

export type ThroughputPolicy = "auto" | "lock-mbps";
export type LatencyPolicy = "auto" | "lock-ms";

export interface FormatPolicy {
    /** Throughput unit policy. Default `"auto"` walks the ladder. The
     *  DPI variant locks to `"lock-mbps"` (never bumps to Gbps/kbps). */
    throughput?: ThroughputPolicy;
    /** Latency unit policy. Default `"auto"` walks the ladder. */
    latency?: LatencyPolicy;
}

// ── Typed wrappers ───────────────────────────────────────────────────

/** Format a Mbps amount. Default policy walks the throughput ladder;
 *  `"lock-mbps"` pins to Mbps regardless of magnitude (DPI variant).
 *
 *  The locked path renders integer Mbps so a 1500 Mbps reading reads as
 *  "1500 Mbps" rather than the toPrecision(3) "1.50e+3 Mbps". DPI is
 *  intended for fast links where sub-Mbps decimals are irrelevant. */
export function formatThroughput(
    mbps: number,
    policy: ThroughputPolicy = "auto",
): UnitInfo {
    if (policy === "lock-mbps") {
        return {
            amount: String(Math.round(mbps)),
            unit: "Mbps",
            rawAmount: mbps,
        };
    }
    const rung = pickRung(mbps, THROUGHPUT_LADDER);
    const scaled = mbps / rung.divisor;
    return {
        amount: stripTrailingZeros(scaled.toPrecision(3)),
        unit: rung.unit,
        // rawAmount is ALWAYS the unscaled Mbps so the smoother + formatter
        // converge on one ladder pass per frame. Pre-W2 this was `scaled`,
        // which caused U1 (the formatter re-laddered the smoothed scaled
        // value, dropping the unit one rung below correct).
        rawAmount: mbps,
    };
}

/** Format a ms amount. Default policy walks the latency ladder;
 *  `"lock-ms"` pins to ms. Latency rounds to integers — sub-second
 *  precision is what humans read for pings. */
export function formatLatency(
    ms: number,
    policy: LatencyPolicy = "auto",
): UnitInfo {
    if (policy === "lock-ms") {
        return { amount: String(Math.round(ms)), unit: "ms", rawAmount: ms };
    }
    const rung = pickRung(ms, LATENCY_LADDER);
    const scaled = ms / rung.divisor;
    return {
        amount: String(Math.round(scaled)),
        unit: rung.unit,
        // rawAmount is ALWAYS the unscaled ms (see formatThroughput note).
        rawAmount: ms,
    };
}

// ── Dispatcher ───────────────────────────────────────────────────────

/** Format a numeric value into `UnitInfo` for display.
 *
 *  Throughput (download/upload) walks the `THROUGHPUT_LADDER`; latency
 *  (ping/jitter) walks the `LATENCY_LADDER`. Jitter rides ping's contract
 *  (the W3 regression-fix). Unknown ids return an empty `UnitInfo`.
 *
 *  Variant coupling stays at the call-site through the optional `policy`
 *  arg — the formatter is variant-agnostic. */
export function formatUnit(
    metricId: MetricId | string,
    amount: number,
    policy: FormatPolicy = {},
): UnitInfo {
    if (!isMetricId(metricId)) return {};

    if (metricId === "download" || metricId === "upload") {
        return formatThroughput(amount, policy.throughput ?? "auto");
    }

    // Latency branch: ping AND jitter both ride this path. The pre-T
    // bug-fix lives here — jitter previously fell into the empty-return
    // below, forcing `MetricPillCluster.formatPill` to exist as a workaround.
    return formatLatency(amount, policy.latency ?? "auto");
}
